import { SHOPIFY_COHORT_ANALYSIS_FAQ_EN } from '../shopifyCohortAnalysisFaqLd';

function faqToMarkdown(
  heading: string,
  rows: readonly { readonly q: string; readonly a: string }[],
): string {
  const chunks: string[] = [`## ${heading}`, ''];
  for (const { q, a } of rows) {
    chunks.push(`### ${q}`, '', a, '', '');
  }
  return chunks.join('\n').trimEnd();
}

const FAQ_EN_BODY = faqToMarkdown('FAQ', SHOPIFY_COHORT_ANALYSIS_FAQ_EN);

/**
 * Long-form article (EN) — Shopify cohort analysis by first-purchase product.
 * Markdown in TS (no CRA raw-import); evitare backtick letterali nel template.
 */
export const SHOPIFY_COHORT_ANALYSIS_REPEAT_PURCHASE_PRODUCTS_EN_BODY =
  `Most Shopify stores optimize acquisition around the wrong question.

They ask:

> “Which product has the highest first-order ROAS?”

That question matters, but it is incomplete.

A better question is:

> “Which product brings in customers who come back, spend more and generate profit after the first order?”

That is where Shopify cohort analysis becomes much more useful. Not just as a retention report, but as an acquisition decision framework.

A product can look great inside Meta Ads or Google Ads because it converts well on the first order. But if the customers acquired through that product never buy again, the real economics can be weak. Another product may look less impressive on day one, but create stronger repeat purchase behavior, higher LTV and better CAC payback.

This article explains how to use cohort analysis to find the products that actually create valuable customers.

---

## What is Shopify cohort analysis?

Shopify cohort analysis groups customers based on a shared starting point, usually the period in which they placed their first order.

For example:

* customers whose first order was in January;
* customers whose first order was in February;
* customers whose first order was in March.

You can then analyze how those groups behave over time: repeat purchases, amount spent, retention rate and other customer metrics.

Shopify already includes a native [Customer cohort analysis](https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/default-reports/customers-reports) report. It can show repeat purchase behavior by first purchase period and visualize cohorts as a heatmap or retention curve.

That is a useful starting point.

But if you run an ecommerce business, the strategic question is often more granular:

> Which first-purchase products create the best customers?

Because not every first order has the same value.

---

## The missing question: which first-purchase products create repeat customers?

Two products can generate the same revenue today and completely different customer value tomorrow.

Imagine these two acquisition products:

| Product | First-order ROAS | Repeat purchase rate | Net LTV |
| --- | ---: | ---: | ---: |
| Product A | 3.2x | 8% | €58 |
| Product B | 2.4x | 28% | €124 |

If you only look at ad platform ROAS, Product A looks better.

But if Product B creates more repeat customers and higher net LTV, Product B may be the better acquisition product.

This is the difference between:

* optimizing for transactions;
* optimizing for customers;
* optimizing for profitable customers.

For Shopify merchants, this matters across paid acquisition, email marketing, merchandising, bundles, retention strategy and inventory planning.

---

## The core metrics to calculate

To analyze repeat purchase behavior by first-purchase product, you need a consistent metric framework.

| Metric | Definition | Why it matters |
| --- | --- | --- |
| First-purchase customers | Customers whose first order included a specific product | Defines the acquisition cohort |
| Repeat customers | Customers in that cohort with at least one later order | Measures whether the product creates returning customers |
| Repeat purchase rate | Repeat customers / first-purchase customers | Shows the product’s retention quality |
| Median days to second order | Median number of days between first and second order | Helps time email, SMS and remarketing flows |
| Gross LTV | Total revenue generated by the customer cohort | Useful but incomplete |
| Net LTV | Revenue minus discounts, refunds, COGS and other costs | Better proxy for customer value |
| CAC payback | Time or number of orders needed to recover acquisition cost | Critical for scaling paid acquisition |
| Inventory cover | Current stock / sales velocity | Prevents scaling products that will stock out too quickly |

The key is to avoid looking at retention in isolation.

A high repeat purchase rate is useful only if the repeat orders are profitable, stock is available and acquisition cost is sustainable.

---

## How to run the analysis

The cleanest way to analyze this is to build the logic from order-level and line-item-level data.

### 1. Identify each customer’s first order

Start by finding the first order date for each customer.

You need:

* customer ID or email;
* order ID;
* order date;
* order revenue;
* discounts;
* refunds;
* line items;
* product ID or SKU;
* product cost, if available.

The goal is to isolate the first order for each customer.

---

### 2. Assign first-purchase products

Once you know each customer’s first order, look at the products included in that first order.

If the first order includes only one product, attribution is simple.

If the first order includes multiple products, you need a rule. For example:

* assign the customer to every product in the first order;
* assign the customer to the highest-value item;
* assign the customer to the primary SKU;
* separate bundles from single products.

For most Shopify stores, assigning the customer to every product in the first order is a practical starting point. It answers the question:

> “When this product appears in a first order, how often does that customer buy again?”

---

### 3. Calculate repeat customers

A customer is a repeat customer if they placed at least one additional order after the first order.

The basic formula is:

    Repeat purchase rate = repeat customers / first-purchase customers

Example:

    52 customers bought Product X in their first order.
    4 of those customers placed at least one later order.
    Repeat purchase rate = 4 / 52 = 7.7%

This does not mean Product X generated four repeat orders.

It means four customers from that first-purchase product cohort became repeat customers.

That distinction matters.

---

### 4. Measure time to second order

Repeat rate alone is not enough.

You also need to know how long it takes for customers to come back.

If the median second order happens after 18 days, your post-purchase flow should be different from a product where the median second order happens after 90 days.

Useful cuts:

* median days to second order;
* average days to second order;
* repeat rate by 30, 60, 90 and 180 days;
* cumulative LTV by month after first order.

This turns retention analysis into an operating system for lifecycle marketing.

---

### 5. Add margin, discounts and refunds

Gross revenue can mislead you.

A product may create repeat customers, but still generate poor economics if:

* first-order discounts are too aggressive;
* refunds are high;
* COGS is high;
* shipping costs destroy contribution margin;
* repeat orders are low-margin.

That is why net LTV is more useful than gross LTV.

A simple version:

    Net LTV = net revenue - discounts - refunds - product costs - variable fulfillment costs

You do not need a perfect finance model to start. Even a directional contribution margin view is better than using revenue alone.

---

### 6. Connect acquisition data

The real breakthrough happens when you connect first-purchase product cohorts to paid acquisition.

For example:

| First-purchase product | CAC | First-order ROAS | Repeat rate | Net LTV | CAC payback |
| --- | --: | ---: | ---: | ---: | --- |
| Product A | €31 | 3.2x | 8% | €58 | Slow |
| Product B | €38 | 2.4x | 28% | €124 | Strong |
| Product C | €22 | 4.1x | 3% | €41 | Weak |

This is where platform ROAS becomes insufficient.

Meta Ads and Google Ads can tell you what happened inside their attribution windows. But they usually do not tell you whether the customers acquired through a product become profitable over time.

That requires Shopify order data, product data, customer history and ad spend in one model. For a related read on blending channels with profit context, see [Meta Ads MCP for Shopify](/blog/meta-ads-mcp-for-shopify).

---

## How to use the insight

Once you know which first-purchase products create repeat customers, you can make better decisions across the business.

### Paid acquisition

Do not scale only the product with the highest first-order ROAS.

Scale the products that generate:

* acceptable CAC;
* strong repeat purchase rate;
* good net LTV;
* enough inventory depth;
* healthy gross margin.

This is especially important for stores with consumables, fashion collections, beauty products, supplements, pet products, food products or any category with repeat purchase potential.

---

### Email and SMS flows

First-purchase product should influence your lifecycle flows.

A customer who first buys a replenishable product may need:

* reorder reminders;
* subscription prompts;
* replenishment timing;
* bundle suggestions.

A customer who first buys an entry-level product may need:

* education;
* product sequencing;
* cross-sell flows;
* higher-margin second-purchase offers.

The first product is a signal. Use it.

---

### Merchandising and bundles

Cohort analysis can show which products are not just best sellers, but best customer starters.

That can influence:

* homepage merchandising;
* landing pages;
* bundles;
* product recommendations;
* post-purchase offers;
* gift-with-purchase strategy.

A product with lower margin on the first order may still be valuable if it starts a strong customer journey.

---

### Inventory planning

Retention insight is only useful if you can fulfill demand.

Before scaling a product that creates strong repeat behavior, check:

* current stock;
* stock cover;
* sell-through rate;
* reorder timing;
* supplier lead time;
* variant-level availability.

Shopify [inventory reports](https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/default-reports/inventory-reports) and [product analytics](https://help.shopify.com/en/manual/products/analytics) can help monitor inventory sold, sell-through rate and days of inventory remaining. The important step is to connect those metrics to customer value.

A product that creates great customers but constantly goes out of stock can cap growth and damage the customer experience.

---

## Common mistakes in Shopify cohort analysis

### Mistake 1: Looking only at revenue

Revenue does not equal customer value.

Always try to move from revenue to contribution margin or net LTV.

---

### Mistake 2: Ignoring sample size

If a product has only 12 first-purchase customers, its repeat rate may be directionally interesting but not statistically reliable.

Use minimum thresholds before making aggressive budget decisions.

As a practical rule, treat anything below 50 first-purchase customers as directional. Above 100, the signal usually becomes more useful, depending on your store volume and category.

---

### Mistake 3: Mixing products, variants and bundles

Product-level analysis can hide important differences.

A black T-shirt and a limited-edition bundle may behave very differently, even if they belong to the same product family.

Decide whether your analysis should run at:

* product level;
* variant level;
* SKU level;
* bundle level;
* category level.

---

### Mistake 4: Ignoring time windows

A product with a 12-month purchase cycle will look weak if you analyze only 30 days of data.

Use time windows that match your category.

For example:

* beauty replenishment: 30–90 days;
* fashion: 60–180 days;
* supplements: 30–60 days;
* furniture: much longer;
* pet food: often highly replenishable.

---

### Mistake 5: Trusting ad platform ROAS as the final answer

Ad platform ROAS is useful, but it is not the whole business model.

It usually misses:

* true blended acquisition cost;
* organic contribution;
* returning customer behavior;
* refunds;
* discounts;
* margin;
* inventory constraints;
* incrementality.

For ecommerce teams, ROAS should be connected to customer economics, not treated as the final KPI.

---

## Shopify native reports vs a profit analytics layer

Shopify’s native cohort and inventory reports are a strong starting point.

They help you understand customer retention, repeat purchase patterns and inventory performance directly inside Shopify.

But most growth decisions require a wider model.

To decide what to scale, you usually need to connect:

* Shopify orders;
* customer cohorts;
* first-purchase products;
* Meta Ads spend;
* Google Ads spend;
* GA4 sessions and conversion behavior;
* discounts;
* refunds;
* COGS;
* margin;
* inventory cover.

That is where a dedicated ecommerce analytics layer becomes useful.

The goal is not to replace Shopify reports. The goal is to connect Shopify data with the rest of the business context.

---

## How Kipify helps

Kipify helps Shopify teams connect store, advertising and analytics data in one place.

Instead of switching between Shopify, Meta Ads, Google Ads, GA4 and spreadsheets, you can build a clearer view of:

* blended ROAS;
* MER;
* product performance;
* custom KPIs;
* margin-aware reporting;
* inventory health;
* retention and repeat purchase analysis.

For cohort analysis, the real value is not just seeing that a customer came back.

The value is knowing:

> which product acquired that customer, how much it cost to acquire them, whether they returned, how much profit they generated and whether you can scale that product without creating an inventory problem.

That is the kind of visibility ecommerce teams need to grow profitably.

---

## Practical checklist

Before you scale a product in paid acquisition, answer these questions:

* Does this product generate repeat customers?
* What is the repeat purchase rate by first-purchase product?
* How many days does it take for customers to place a second order?
* What is the net LTV, not just gross revenue?
* What is the CAC payback?
* Are discounts or refunds damaging the economics?
* Is there enough inventory to scale?
* Does the product create profitable second and third orders?
* Is this product a best seller, or a true customer starter?

If you cannot answer these questions, you are probably scaling based on incomplete data.

---

` +
  FAQ_EN_BODY +
  `

---

## Final thought

Your best-selling product is not always your best acquisition product.

The product that gets the first order may not be the product that creates the best customer.

Shopify cohort analysis becomes much more powerful when you connect it to first-purchase product, paid acquisition, margin and inventory.

That is how you move from reporting what happened to understanding what to scale next.

---

## Next step

**Want to know which products actually create repeat customers?** [Get a free ecommerce analytics audit](/landing-adv/en) — Kipify connects Shopify, Meta Ads, Google Ads and GA4 so you can analyze retention, LTV, margins and inventory in one place.
`;
